import { FormikProps } from 'formik';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import cc from 'classcat';
import { useDebounce } from '../../../hooks/useDebounce';

interface InputSearchFormikDebounceProps<T> {
    id: string;
    placeholder?: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    context: FormikProps<T>;
    className: string;
    rightAlignErrorMsg?: boolean;
    isCloseButtonVisible: boolean;
    closeButtonOnClick: () => void;
    searchButtonOnClick?: () => void;
    debounceDelay?: number;
    regexPattern?: RegExp;
    onBlur?: () => void;
    shouldFocus?: boolean;
}

export const InputSearchFormikDebounce = <T extends object>({
    id,
    placeholder,
    onChange,
    context,
    className,
    rightAlignErrorMsg,
    isCloseButtonVisible,
    closeButtonOnClick,
    searchButtonOnClick,
    regexPattern,
    debounceDelay = 300,
    onBlur,
    shouldFocus,
}: InputSearchFormikDebounceProps<T>) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { values, handleChange, errors, touched } = context;
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const debouncedOnChange = useDebounce((e: ChangeEvent<HTMLInputElement>) => {
        onChange?.(e);
    }, debounceDelay);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (regexPattern && !regexPattern.test(value)) return;

        handleChange(e);
        if (value.trim() === '') closeButtonOnClick();
        else debouncedOnChange(e);
    };

    const isError = touched[id as keyof T] && errors[id as keyof T];

    const handleBlur = () => {
        setIsFocused(false);
        if (onBlur && (values[id as keyof T] as string)?.trim() === '') {
            onBlur();
        }
    };

    useEffect(() => {
        if (shouldFocus) {
            inputRef.current?.focus();
            setIsFocused(true);
        }
    }, [shouldFocus]);

    return (
        <div className={cc(['input-search-formik-dobunce', className])}>
            <div>
                <input
                    ref={inputRef}
                    id={id}
                    name={id}
                    value={values[id as keyof T] as string}
                    placeholder={placeholder}
                    autoComplete="off"
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                    maxLength={250}
                    className={cc(['input-search-formik-dobunce__field', { 'input-search-formik-dobunce__field--error': isError }])}
                    type="text"
                />
                {isError && isFocused && (
                    <span
                        className={cc([
                            'input-search-formik-dobunce__error-msg',
                            rightAlignErrorMsg && 'input-search-formik-dobunce__error-msg--right',
                        ])}
                    >
                        {errors[id as keyof T] as string}
                    </span>
                )}
            </div>
            {isCloseButtonVisible && (
                <button
                    type="button"
                    onClick={closeButtonOnClick}
                    className="input-search-formik-dobunce__button input-search-formik-dobunce__close-button"
                >
                    <img src="/svg/close-x.svg" alt="empty" />
                </button>
            )}
            <button
                type="button"
                onClick={searchButtonOnClick}
                className="input-search-formik-dobunce__button input-search-formik-dobunce__search-button"
            >
                <img src={isFocused ? '/svg/common/search-blue.svg' : '/svg/common/search.svg'} alt="search" />
            </button>
        </div>
    );
};
